/** @jsxImportSource @emotion/react */
import {
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
} from "@chakra-ui/react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Editor } from "@tiptap/react";
import dayjs from "dayjs";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { BsThreeDots } from "react-icons/bs";
import { FaEye } from "react-icons/fa";
import { IoOptionsSharp } from "react-icons/io5";
import { toast } from "react-toastify";
import { useRecoilState, useRecoilValue } from "recoil";
import EmailSettingsIcon from "../../assets/icon/email-settings";
import { ActiveEmailAtom } from "../../atoms/ActiveEmailAtom";
import { FullScreenAtom } from "../../atoms/FullScreenAtom";
import { LabelAtom } from "../../atoms/LabelAtom";
import { RecipientAtom } from "../../atoms/RecipientAtom";
import { CountedSavedSearchesAtom } from "../../atoms/SavedSearches";
import { InternalUserAtom, UserAtom } from "../../atoms/UserAtom";
import { WordsLengthAtom } from "../../atoms/WordsLengthAtom";
import useClickOutside from "../../hook/useClickOutside";
import { useDefaultAlias } from "../../hook/useDefaultAlias";
import useMediaQuery from "../../hook/useMediaQuery";
import { Flex } from "../../styles/general";
import { IEmail } from "../../types/EmailTypes";
import { IHelpContexts } from "../../types/HelpVideosTypes";
import { ILabel } from "../../types/LabelTypes";
import IconBerserkerMail from "../Icon";
import IconButton from "../IconButton";
import LabelSelect from "../Label/LabelSelects";
import CalendarLinks from "../Modal/container/CalendarLinks";
import EmailSettings from "../Modal/container/EmailSettings/EmailSettings";
import PreviewEmail from "../Modal/container/PreviewEmail";
import { useModal } from "../Modal/Modal";
import PlayVideoPopover from "../PlayVideoPopover";
import StyledButton from "../StyledButton";
import WhatsNewButton from "../WhatsNewButton";
import MergeFields from "./MergeFields";

const MoreOptionPopoverContainer = styled.div`
  height: fit-content;

  background: white;

  position: absolute;
  top: 46px;
  right: 0;
  z-index: 20;

  border-radius: 5px;
  box-shadow: 0px 3px 6px #00000026;

  border: 1px solid #d4d4d4;
  border-bottom: none;
  border-top: none;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  div,
  button {
    width: 100%;
  }

  @media screen and (max-width: 600px) {
    width: 200px;
  }
`;

const ButtonPopoverStyled = styled.div`
  width: 100%;
  height: 50px;
  padding: 0 10px;
  position: relative;
  text-align: center;
  align-items: center;
  display: flex;
  justify-content: center;

  font-size: 15px;
  font-weight: 600;
  color: #555555;

  cursor: pointer;
  user-select: none;
  font-weight: 600;
  font-size: 15px;

  transition: all 0.3s ease-in-out;

  &:hover {
    background: #efefef;
  }
`;

const bottomButtonsCss = css`
  width: 5em;
  padding: 0 10px;
  position: relative;
  color: #555555;
  text-align: center;
  align-items: center;
  display: flex;
  justify-content: center;
  height: 50px;
  cursor: pointer;
  user-select: none;
  font-weight: 600;
  font-size: 15px;

  transition: all 0.3s ease-in-out;

  &:hover {
    background: #efefef;
  }
`;

const bottomActionsCss = css`
  width: 110px;
  .merge-fields-container {
    top: -220px;
  }
`;

const Wrapper = styled.div`
  width: calc(100% - 60px);
  background: #ffffff;
  border-bottom: 1px solid #dddddd;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  position: fixed;
  z-index: 998;

  @media screen and (max-width: 960px) {
    width: 100%;

    flex-direction: column;
    align-items: flex-start;
    grid-gap: 24px;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;

  grid-gap: 15px;

  @media screen and (max-width: 600px) {
    grid-gap: 10px;
  }

  .icon-button {
    min-width: 42px;
    max-width: 42px;
    min-height: 42px;
    max-height: 42px;

    flex-shrink: 0;

    display: flex;
    align-items: center;
    justify-content: center;

    background: #fff;

    border-radius: 5px;
    border: 1px solid #dddddd;

    cursor: pointer;

    svg {
      width: 20px;
      height: 20px;

      color: #555555;
    }

    .tooltip-text {
      font-size: 14px;
      font-weight: 600;
      letter-spacing: 0px;
      color: #ffffff;
    }
  }
`;

const IconButtonWrapper = styled.div`
  min-width: 42px;
  max-width: 42px;
  min-height: 42px;
  max-height: 42px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  background: #fff;

  border-radius: 5px;
  border: 1px solid #dddddd;

  cursor: pointer;

  position: relative;

  svg {
    width: 20px;
    height: 20px;

    color: #555555;
  }

  .tooltip-text {
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0px;
    color: #ffffff;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  cursor: default;
`;

const TopBarTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #333333;

  @media screen and (min-width: 600px) {
    font-size: 20px;
  }
`;

const TopBarSubTitle = styled.div`
  font-size: 14px;
  margin-top: 3px;
  color: #707070;
`;

const PreviewModalTitle = styled.div`
  font-size: 18px;
`;

interface Props {
  body?: string;
  subject?: string;
  editor?: Editor;
  onOpen: () => void;
}

const TopBar = (props: Props): JSX.Element => {
  const { editor, onOpen } = props;

  const modal = useModal();
  const router = useRouter();
  const user = useRecoilValue(UserAtom);
  const internalUser = useRecoilValue(InternalUserAtom);
  const recipient = useRecoilValue(RecipientAtom);
  const [activeEmail, setActiveEmail] = useRecoilState(ActiveEmailAtom);
  const labels = useRecoilValue(LabelAtom);
  const countedSavedSearches = useRecoilValue(CountedSavedSearchesAtom);
  const isLargerThan600 = useMediaQuery("(min-width: 600px)");
  const [openDropView, setOpenDropView] = useState<boolean>(false);
  const [showMergeMenu, setShowMergeMenu] = useState(false);
  const wrapperRef = useClickOutside(() => setOpenDropView(false));
  const mergeFieldsRef = useClickOutside(() => {
    setShowMergeMenu(false);
  });
  const words = useRecoilValue(WordsLengthAtom);
  const [fullScreen, setFullScreen] = useRecoilState(FullScreenAtom);

  const { defaultAlias } = useDefaultAlias({
    user,
    internalUser,
  });

  useEffect(() => {
    if (!countedSavedSearches.length) return;
    if (!recipient) return;

    const selectedSavedSearch = countedSavedSearches.find(
      ({ id }) => recipient.searchId === id
    );

    if (!selectedSavedSearch) return;

    // If I'm coming by the duplicate email, activeEmail don't need to remember
    // the current Saved Search labels. He needs to maintain the labels setted when
    // the email was scheduled/sent

    const urlParams = new URL(window.location as any).searchParams;
    if (urlParams.get("duplicate") === "true") return;

    // Since activeEmail is cleared when new-email.tsx is unmounted,
    // I need to, manually, load activeEmail labels(if a saved search
    // was previously selected)

    setActiveEmail((prevActiveEmail) => ({
      ...prevActiveEmail,
      labels: selectedSavedSearch?.labels,
    }));
  }, [countedSavedSearches, recipient]);

  function selectLabelHandler(label: ILabel) {
    if (activeEmail) {
      const isLabelIn = activeEmail.labels.find(
        (activeLabel) => activeLabel === label._id
      );

      const newLabelsArray = isLabelIn
        ? // If label in, filters it out
          activeEmail.labels.filter((_id) => {
            return _id !== label._id;
          })
        : // If label is not in, add it in
          [...activeEmail.labels, label._id];

      //@ts-ignore
      setActiveEmail((prevActiveEmail) => ({
        //@ts-ignore
        ...(prevActiveEmail as IEmail),
        labels: newLabelsArray,
      }));
    }
  }

  useEffect(() => {
    global.ps_config = {
      workspace_id: "f8ea5b12-675a-43e1-af7e-19f0464c62d8",
    };

    let script = document.createElement("script");
    script.setAttribute("type", "text/javascript");
    script.setAttribute("src", "https://cdn.loopedin.io/js/widget.js?v=0.8");
    document.getElementsByTagName("head")[0].appendChild(script);
  }, []);

  async function handleCopyClick() {
    // Get body element
    let html = activeEmail.body;

    html = `<p style="margin: 0px">${activeEmail.subject}</p>${html}`;

    const htmlBlob = new Blob([html], { type: "text/html" });

    let text = html;

    text = text.replace(/<style([\s\S]*?)<\/style>/gi, "");
    text = text.replace(/<script([\s\S]*?)<\/script>/gi, "");
    text = text.replace(/<\/div>/gi, "\n");
    text = text.replace(/<\/li>/gi, "\n");
    text = text.replace(/<li>/gi, "  *  ");
    text = text.replace(/<\/ul>/gi, "\n");
    text = text.replace(/<\/ol>/gi, "\n");
    text = text.replace(/<\/p>/gi, "\n");
    text = text.replace(/<\/h[1-3]>/gi, "\n");
    text = text.replace(/<br\s*[\/]?>/gi, "\n");
    text = text.replace(/<[^>]+>/gi, "");

    const textBlob = new Blob([text], { type: "text/plain" });

    // @ts-ignore
    const item = new ClipboardItem({
      "text/plain": textBlob,
      "text/html": htmlBlob,
    });

    // @ts-ignore
    await navigator.clipboard.write([item]);

    toast("Content successfully copied to the clipboard!", {
      type: "success",
    });
  }

  if (!user) return <div />;
  return (
    <Wrapper>
      <TitleContainer>
        <Flex>
          <TopBarTitle
            css={css`
              color: #707070;
              cursor: pointer;
            `}
            onClick={() => {
              if (!isLargerThan600) return;
              router.push("/emails");
            }}
          >
            All Emails
          </TopBarTitle>
          <div style={{ margin: "0px 8px 0px 10px" }}>
            <IconBerserkerMail name="navigation-arrow-right" />
          </div>
          <TopBarTitle>
            {window.location.href.includes("edit")
              ? "Edit Email"
              : "New Email Broadcast"}
          </TopBarTitle>
          <LabelSelect
            activeLabels={
              labels?.filter((l) => {
                if (activeEmail && activeEmail.labels)
                  return activeEmail.labels.includes(l._id);
                else return false;
              }) || []
            }
            selectLabelHandler={selectLabelHandler}
          />
        </Flex>
        <TopBarSubTitle>
          {!editor?.isEmpty || props.subject
            ? "Last Saved " + dayjs().format("MMM D, YYYY [@] h:mm a (z)")
            : "Create a new email"}
        </TopBarSubTitle>
      </TitleContainer>

      <ButtonsContainer>
        {isLargerThan600 && (
          <PlayVideoPopover contextTag={IHelpContexts.EmailBroadcast} />
        )}
        {isLargerThan600 && <WhatsNewButton />}
        {isLargerThan600 ? (
          <StyledButton
            onClick={onOpen}
            inverted
            css={css`
              width: fit-content;
              height: 42px;
              padding: 12px;
              word-break: keep-all;

              @media screen and (min-width: 1080px) {
                display: none;
              }
            `}
          >
            <IoOptionsSharp style={{ marginRight: "5px" }} />
            Schedule Options
          </StyledButton>
        ) : (
          <IconButtonWrapper onClick={onOpen}>
            <IoOptionsSharp />
          </IconButtonWrapper>
        )}

        {!editor?.isEmpty &&
          props.subject &&
          (isLargerThan600 ? (
            <IconButton
              css={css`
                width: fit-content;
                height: 42px;
              `}
              onClick={() => {
                modal.pushModal(
                  <PreviewEmail
                    editor={editor}
                    subject={props.subject as string}
                    body={props.body as string}
                    user={user}
                    emailInfo={{
                      footer: recipient.footerId
                        ? user.emailOptions.footers.find(
                            (f) => f.id === recipient.footerId
                          )
                        : user.emailOptions.footers[0],
                      alias: activeEmail?.aliasId
                        ? user.emailOptions.aliases.find(
                            (a) => a.id === activeEmail?.aliasId
                          )
                        : defaultAlias,
                    }}
                  />,

                  <PreviewModalTitle>Preview</PreviewModalTitle>
                );
              }}
              icon={"preview"}
            >
              Preview
            </IconButton>
          ) : (
            <IconButtonWrapper
              onClick={() => {
                modal.pushModal(
                  <PreviewEmail
                    editor={editor}
                    subject={props.subject as string}
                    body={props.body as string}
                    user={user}
                    emailInfo={{
                      footer: recipient.footerId
                        ? user.emailOptions.footers.find(
                            (f) => f.id === recipient.footerId
                          )
                        : user.emailOptions.footers[0],
                      alias: activeEmail?.aliasId
                        ? user.emailOptions.aliases.find(
                            (a) => a.id === activeEmail?.aliasId
                          )
                        : defaultAlias,
                    }}
                  />,

                  <PreviewModalTitle>Preview</PreviewModalTitle>
                );
              }}
            >
              <FaEye />
            </IconButtonWrapper>
          ))}

        {isLargerThan600 ? (
          <IconButton
            icon="email-settings"
            onClick={() => {
              modal.pushModal(<EmailSettings />, "Email Settings", false, true);
            }}
            css={css`
              width: fit-content;
              height: 42px;
            `}
          >
            Settings
          </IconButton>
        ) : (
          <IconButtonWrapper
            onClick={() => {
              modal.pushModal(<EmailSettings />, "Email Settings", false, true);
            }}
          >
            <EmailSettingsIcon />
          </IconButtonWrapper>
        )}

        {!isLargerThan600 && (
          <Popover placement="right-start">
            <PopoverTrigger>
              <IconButtonWrapper>
                <BsThreeDots />
              </IconButtonWrapper>
            </PopoverTrigger>
            <PopoverContent w="110px">
              <PopoverArrow />
              <PopoverBody p="0">
                <ButtonPopoverStyled onClick={handleCopyClick}>
                  Copy All
                </ButtonPopoverStyled>
                <MergeFields editor={editor}>
                  <ButtonPopoverStyled
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    ref={mergeFieldsRef}
                  >
                    Merge
                  </ButtonPopoverStyled>
                </MergeFields>
                <ButtonPopoverStyled
                  onClick={() => {
                    modal.pushModal(
                      <CalendarLinks editor={editor} />,
                      "Create Event"
                    );
                  }}
                >
                  Create Event
                </ButtonPopoverStyled>
                <ButtonPopoverStyled
                  css={css`
                    @media screen and (max-width: 640px) {
                      display: none;
                    }
                  `}
                  onClick={() => {
                    setFullScreen(!fullScreen);
                  }}
                >
                  Full Screen
                </ButtonPopoverStyled>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        )}
      </ButtonsContainer>
    </Wrapper>
  );
};

export default TopBar;
